import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { ProtectedRoute } from './components/ProtectedRoute'
import { AdminProtectedRoute } from './components/AdminProtectedRoute'
import { useEffect, useState } from 'react'
import AppLayout from './components/AppLayout'
import { ScrollToTop } from './components/ScrollToTop'
import { DailyActivityTracker } from './components/DailyActivityTracker'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import BetaSignupPage from './pages/BetaSignupPage'
import InvitedSignupPage from './pages/InvitedSignupPage'
import Dashboard from './pages/Dashboard'
import LandingPage from './pages/LandingPage'
import OAuthCallback from './pages/OAuthCallback'
import AdminHomePage from './pages/AdminHomePage'
import AccountAdminPage from './pages/AccountAdminPage'
import FlowDocsLabsPage from './pages/FlowDocsLabsPage'
import IndustryInsightsPage from './pages/admin/IndustryInsightsPage'
import ReactivationDashboard from './components/admin/ReactivationDashboard'
import SalesforceCallback from './pages/SalesforceCallback'
import AuthCallback from './pages/AuthCallback'
import OrgsPage from './pages/OrgsPage'
import WorkspacesPage from './pages/WorkspacesPage'
import FlowsPage from './pages/FlowsPage'
import FlowDetailPage from './pages/FlowDetailPage'
import ObjectsPage from './pages/ObjectsPage'
import FieldTestPage from './pages/FieldTestPage'
import SettingsPage from './pages/SettingsPage'
import OrgSettingsPage from './pages/OrgSettingsPage'
import AuthDebug from './pages/AuthDebug'
import AuthDebugPage from './pages/AuthDebugPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import SalesforceFlowConnectorPage from './pages/SalesforceFlowConnectorPage'
import AcceptTransferPage from './pages/AcceptTransferPage'
import CustomerTestPage from './pages/CustomerTestPage'
import CustomerTestThankYouPage from './pages/CustomerTestThankYouPage'
import TellMeWhenPage from './pages/TellMeWhenPage'
import WaitlistPage from './pages/WaitlistPage'
import WaitlistThankYouPage from './pages/WaitlistThankYouPage'
import UpgradePage from './pages/UpgradePage'
import SubscriptionPage from './pages/SubscriptionPage'
import PlansPageRedesign from './pages/PlansPageRedesign'
import BetaAccessPage from './pages/BetaAccessPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import UpdatePasswordPage from './pages/UpdatePasswordPage'
import SetPasswordPage from './pages/SetPasswordPage'
import AuthVerifyHandler from './pages/AuthVerifyHandler'
import HelpPage from './pages/help/HelpPage'
import { PasswordCheck } from './components/PasswordCheck'
import QuickStartPage from './pages/help/QuickStartPage'
import SyncMethodsPage from './pages/help/SyncMethodsPage'
import DashboardGuidePage from './pages/help/DashboardGuidePage'
import OrgsGuidePage from './pages/help/OrgsGuidePage'
import FlowsGuidePage from './pages/help/FlowsGuidePage'
import EntryCriteriaGuidePage from './pages/help/EntryCriteriaGuidePage'
import FieldsGuidePage from './pages/help/FieldsGuidePage'
import FlowHeatAnalysisPage from './pages/help/FlowHeatAnalysisPage'
import BestPracticesPage from './pages/help/BestPracticesPage'
import AvoidHardcodedIdsPage from './pages/help/best-practices/AvoidHardcodedIdsPage'
import UserAssignmentPage from './pages/help/best-practices/UserAssignmentPage'
import RecordTypeAssignmentPage from './pages/help/best-practices/RecordTypeAssignmentPage'
import CustomMetadataPage from './pages/help/best-practices/CustomMetadataPage'
import EntryCriteriaBestPracticePage from './pages/help/best-practices/EntryCriteriaPage'
import QueriesInLoopsPage from './pages/help/QueriesInLoopsPage'
import DmlInLoopsPage from './pages/help/DmlInLoopsPage'
import BulkificationPage from './pages/help/BulkificationPage'
import IndexedQueriesPage from './pages/help/IndexedQueriesPage'
import ErrorHandlingPage from './pages/help/ErrorHandlingPage'
import UnfilteredQueriesPage from './pages/help/UnfilteredQueriesPage'
import FlowComplexityPage from './pages/help/FlowComplexityPage'
import FlowDescriptionPage from './pages/help/FlowDescriptionPage'
import FlowNamingPage from './pages/help/FlowNamingPage'
import ContactSupportPage from './pages/help/ContactSupportPage'
import SupportThankYouPage from './pages/SupportThankYouPage'
import PreferencesPage from './pages/PreferencesPage'
import EntryCriteriaPage from './pages/EntryCriteriaPage'
import ConnectSalesforceEnhanced from './pages/ConnectSalesforceEnhanced'
import SecurityPage from './pages/SecurityPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import AlternateHomePage from './pages/AlternateHomePage'
import { NewLandingPage } from './pages/NewLandingPage'
import GuidedTour from './components/GuidedTour'
import { ToastProvider } from './context/ToastContext'

// Screenshot components (in /s directory)
import ScreenshotDashboard from '../s/Dashboard'

// Component to handle route changes without excessive logging
function RouteHandler({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  
  useEffect(() => {
    // Removed console logging to prevent render loop issues
  }, [location]);
  
  return <>{children}</>;
}

function App() {
  const { user, isLoading, signOut } = useAuth()
  const location = useLocation()
  
  // Handle OAuth state errors at the top level
  useEffect(() => {
    // Check for OAuth state errors in the URL
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    const errorCode = urlParams.get('error_code');
    
    if (error === 'invalid_request' && errorCode === 'bad_oauth_state') {
      console.error('Detected OAuth state error at application root:', {
        error,
        errorCode,
        errorDescription: urlParams.get('error_description'),
        path: location.pathname,
        fullUrl: window.location.href
      });
      
      // Clear all auth storage
      console.log('Clearing all auth storage due to OAuth state error');
      
      // Clear localStorage auth data
      const localStorageKeys = Object.keys(localStorage).filter(k => 
        k.startsWith('sb-') || k.startsWith('supabase') || k.startsWith('sf_')
      );
      console.log('Clearing localStorage keys:', localStorageKeys);
      localStorageKeys.forEach(key => localStorage.removeItem(key));
      
      // Clear sessionStorage auth data
      const sessionStorageKeys = Object.keys(sessionStorage).filter(k => 
        k.startsWith('sb-') || k.startsWith('supabase') || k.startsWith('sf_')
      );
      console.log('Clearing sessionStorage keys:', sessionStorageKeys);
      sessionStorageKeys.forEach(key => sessionStorage.removeItem(key));
      
      // Force sign out
      signOut().catch(e => console.error('Error signing out:', e));
      
      // Redirect to login with error message
      window.location.href = '/login?error=oauth_state&message=Your authentication session expired. Please log in again.';
    }
  }, [location, signOut]);
  
  // Clean session storage on main navigation
  useEffect(() => {
    // Only clean storage when needed, avoid excessive logging
    if (!location.pathname.includes('callback')) {
      sessionStorage.removeItem('oauth_callback_processed');
      sessionStorage.removeItem('sf_callback_processed');
    }
  }, [location])
  
  // Show loading state when authentication is being checked
  if (isLoading && location.pathname !== '/') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <PasswordCheck>
        <AppLayout>
          <ScrollToTop />
          <DailyActivityTracker />
          <GuidedTour />
          <Routes>
      <Route path="/" element={
        user ? <Navigate to="/dashboard" replace /> : <NewLandingPage />
      } />
      <Route path="/login" element={
        user ? <Navigate to="/dashboard" replace /> : <LoginPage />
      } />
      <Route path="/signup" element={
        user ? <Navigate to="/dashboard" replace /> : <SignupPage />
      } />
      <Route path="/beta" element={
        user ? <Navigate to="/dashboard" replace /> : <BetaSignupPage />
      } />
      <Route path="/invited-signup" element={
        user ? <Navigate to="/dashboard" replace /> : <InvitedSignupPage />
      } />
      {/* Legacy dashboard route - redirect to first org */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      {/* New org-specific dashboard route */}
      <Route path="/:orgId/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/orgs" element={
        <ProtectedRoute>
          <OrgsPage />
        </ProtectedRoute>
      } />
      <Route path="/:orgId/orgs" element={
        <ProtectedRoute>
          <OrgsPage />
        </ProtectedRoute>
      } />
      {/* Legacy workspaces route */}
      <Route path="/workspaces" element={
        <ProtectedRoute>
          <WorkspacesPage />
        </ProtectedRoute>
      } />
      {/* New org-specific workspaces route */}
      <Route path="/:orgId/workspaces" element={
        <ProtectedRoute>
          <WorkspacesPage />
        </ProtectedRoute>
      } />
      <Route path="/connect-salesforce" element={
        <ProtectedRoute>
          <ConnectSalesforceEnhanced />
        </ProtectedRoute>
      } />
      {/* Legacy routes with query params - redirect to new format */}
      <Route path="/flows" element={
        <ProtectedRoute>
          <FlowsPage />
        </ProtectedRoute>
      } />
      <Route path="/entry-criteria" element={
        <ProtectedRoute>
          <EntryCriteriaPage />
        </ProtectedRoute>
      } />
      {/* Legacy redirect from /triggers to /entry-criteria */}
      <Route path="/triggers" element={
        <ProtectedRoute>
          <EntryCriteriaPage />
        </ProtectedRoute>
      } />
      <Route path="/fields" element={
        <ProtectedRoute>
          <ObjectsPage />
        </ProtectedRoute>
      } />
      
      {/* New org-specific routes */}
      <Route path="/:orgId/flows" element={
        <ProtectedRoute>
          <FlowsPage />
        </ProtectedRoute>
      } />
      <Route path="/:orgId/entry-criteria" element={
        <ProtectedRoute>
          <EntryCriteriaPage />
        </ProtectedRoute>
      } />
      {/* Legacy redirect from /triggers to /entry-criteria */}
      <Route path="/:orgId/triggers" element={
        <ProtectedRoute>
          <EntryCriteriaPage />
        </ProtectedRoute>
      } />
      <Route path="/:orgId/fields" element={
        <ProtectedRoute>
          <ObjectsPage />
        </ProtectedRoute>
      } />
      {/* Redirect old /objects path to /fields */}
      <Route path="/objects" element={<Navigate to="/fields" replace />} />
      <Route path="/:orgId/objects" element={<Navigate to="/:orgId/fields" replace />} />
      
      {/* Flow detail routes */}
      <Route path="/flows/:orgId/:flowId" element={
        <ProtectedRoute>
          <FlowDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/:orgId/flow/:flowId" element={
        <ProtectedRoute>
          <FlowDetailPage />
        </ProtectedRoute>
      } />
      {/* Legacy settings route */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      {/* New org-specific settings route */}
      <Route path="/:orgId/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      {/* Pricing/billing routes */}
      <Route path="/plans" element={<UpgradePage />} />
      {/* Legacy upgrade route - redirect to /plans */}
      <Route path="/upgrade" element={<Navigate to="/plans" replace />} />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      {/* New redesigned pages for testing */}
      <Route path="/plans2" element={<PlansPageRedesign />} />
      {/* Beta access route */}
      <Route path="/beta-access" element={
        <ProtectedRoute>
          <BetaAccessPage />
        </ProtectedRoute>
      } />
      
      {/* Admin routes - secured for system administrators only */}
      <Route path="/admin" element={
        <AdminProtectedRoute>
          <AdminHomePage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/accounts" element={
        <AdminProtectedRoute>
          <AccountAdminPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/labs" element={
        <AdminProtectedRoute>
          <FlowDocsLabsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/industry-insights" element={
        <AdminProtectedRoute>
          <IndustryInsightsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/reactivation" element={
        <AdminProtectedRoute>
          <ReactivationDashboard />
        </AdminProtectedRoute>
      } />
      {/* Preferences page */}
      <Route path="/preferences" element={
        <ProtectedRoute>
          <PreferencesPage />
        </ProtectedRoute>
      } />
      {/* Legacy org-settings route - redirect to new format */}
      <Route path="/org-settings/:orgId" element={
        <ProtectedRoute>
          <OrgSettingsPage />
        </ProtectedRoute>
      } />
      {/* New org-specific settings route */}
      <Route path="/:orgId/org-settings" element={
        <ProtectedRoute>
          <OrgSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/field-test" element={
        <ProtectedRoute>
          <FieldTestPage />
        </ProtectedRoute>
      } />
      <Route path="/invitation/:token" element={<AcceptInvitationPage />} />
      <Route path="/accept-transfer" element={<AcceptTransferPage />} />
      <Route path="/customer-test" element={<CustomerTestPage />} />
      <Route path="/customer-test-thank-you" element={<CustomerTestThankYouPage />} />
      <Route path="/tell-me-when" element={<TellMeWhenPage />} />
      <Route path="/public-beta" element={<Navigate to="/beta" replace />} />
      <Route path="/waitlist" element={<Navigate to="/beta" replace />} />
      <Route path="/waitlist-thank-you" element={<WaitlistThankYouPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/update-password" element={<UpdatePasswordPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/auth/v1/verify" element={<AuthVerifyHandler />} />
      {/* Hidden marketing page for testing */}
      <Route path="/alt-home" element={<AlternateHomePage />} />
      <Route path="/new-landing" element={<NewLandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Add a hash-specific route to properly capture OAuth redirects */}
      <Route path="/auth/callback#" element={<AuthCallback />} />
      <Route path="/oauth-callback" element={<OAuthCallback />} />
      <Route path="/salesforce-callback" element={<SalesforceCallback />} />
      <Route path="/auth-debug" element={<AuthDebug />} />
      <Route path="/debug" element={<AuthDebugPage />} />
      <Route path="/salesforce-flow-connector" element={
        <ProtectedRoute>
          <SalesforceFlowConnectorPage />
        </ProtectedRoute>
      } />
      {/* Help pages - accessible to everyone */}
      {/* Legacy help routes */}
      <Route path="/help" element={<HelpPage />} />
      <Route path="/help/quick-start" element={<QuickStartPage />} />
      <Route path="/help/sync-methods" element={<SyncMethodsPage />} />
      <Route path="/help/dashboard-guide" element={<DashboardGuidePage />} />
      <Route path="/help/orgs-guide" element={<OrgsGuidePage />} />
      <Route path="/help/flows-guide" element={<FlowsGuidePage />} />
      <Route path="/help/entry-criteria-guide" element={<EntryCriteriaGuidePage />} />
      {/* Legacy redirect */}
      <Route path="/help/triggers-guide" element={<EntryCriteriaGuidePage />} />
      <Route path="/help/fields-guide" element={<FieldsGuidePage />} />
      <Route path="/help/flow-heat-analysis" element={<FlowHeatAnalysisPage />} />
      <Route path="/help/best-practices" element={<BestPracticesPage />} />
      <Route path="/help/best-practices/avoid-hardcoded-ids" element={<AvoidHardcodedIdsPage />} />
      <Route path="/help/best-practices/user-assignment" element={<UserAssignmentPage />} />
      <Route path="/help/best-practices/record-type-assignment" element={<RecordTypeAssignmentPage />} />
      <Route path="/help/best-practices/custom-metadata" element={<CustomMetadataPage />} />
      <Route path="/help/best-practices/entry-criteria" element={<EntryCriteriaBestPracticePage />} />
      <Route path="/help/queries-in-loops" element={<QueriesInLoopsPage />} />
      <Route path="/help/dml-in-loops" element={<DmlInLoopsPage />} />
      <Route path="/help/bulkification" element={<BulkificationPage />} />
      <Route path="/help/indexed-queries" element={<IndexedQueriesPage />} />
      <Route path="/help/error-handling" element={<ErrorHandlingPage />} />
      <Route path="/help/unfiltered-queries" element={<UnfilteredQueriesPage />} />
      <Route path="/help/flow-complexity" element={<FlowComplexityPage />} />
      <Route path="/help/flow-description" element={<FlowDescriptionPage />} />
      <Route path="/help/flow-naming" element={<FlowNamingPage />} />
      <Route path="/help/contact-support" element={<ContactSupportPage />} />
      <Route path="/support-thank-you" element={<SupportThankYouPage />} />
      
      {/* Org-specific help routes */}
      <Route path="/:orgId/help" element={<HelpPage />} />
      <Route path="/:orgId/help/quick-start" element={<QuickStartPage />} />
      <Route path="/:orgId/help/sync-methods" element={<SyncMethodsPage />} />
      <Route path="/:orgId/help/dashboard-guide" element={<DashboardGuidePage />} />
      <Route path="/:orgId/help/orgs-guide" element={<OrgsGuidePage />} />
      <Route path="/:orgId/help/flows-guide" element={<FlowsGuidePage />} />
      <Route path="/:orgId/help/entry-criteria-guide" element={<EntryCriteriaGuidePage />} />
      {/* Legacy redirect */}
      <Route path="/:orgId/help/triggers-guide" element={<EntryCriteriaGuidePage />} />
      <Route path="/:orgId/help/fields-guide" element={<FieldsGuidePage />} />
      <Route path="/:orgId/help/flow-heat-analysis" element={<FlowHeatAnalysisPage />} />
      <Route path="/:orgId/help/best-practices" element={<BestPracticesPage />} />
      <Route path="/:orgId/help/best-practices/avoid-hardcoded-ids" element={<AvoidHardcodedIdsPage />} />
      <Route path="/:orgId/help/best-practices/user-assignment" element={<UserAssignmentPage />} />
      <Route path="/:orgId/help/best-practices/record-type-assignment" element={<RecordTypeAssignmentPage />} />
      <Route path="/:orgId/help/best-practices/custom-metadata" element={<CustomMetadataPage />} />
      <Route path="/:orgId/help/best-practices/entry-criteria" element={<EntryCriteriaBestPracticePage />} />
      <Route path="/:orgId/help/queries-in-loops" element={<QueriesInLoopsPage />} />
      <Route path="/:orgId/help/dml-in-loops" element={<DmlInLoopsPage />} />
      <Route path="/:orgId/help/bulkification" element={<BulkificationPage />} />
      <Route path="/:orgId/help/indexed-queries" element={<IndexedQueriesPage />} />
      <Route path="/:orgId/help/error-handling" element={<ErrorHandlingPage />} />
      <Route path="/:orgId/help/unfiltered-queries" element={<UnfilteredQueriesPage />} />
      <Route path="/:orgId/help/flow-complexity" element={<FlowComplexityPage />} />
      <Route path="/:orgId/help/flow-description" element={<FlowDescriptionPage />} />
      <Route path="/:orgId/help/flow-naming" element={<FlowNamingPage />} />
      <Route path="/:orgId/help/contact-support" element={<ContactSupportPage />} />
      <Route path="/:orgId/support-thank-you" element={<SupportThankYouPage />} />
      
      {/* Legal pages - public */}
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      
      {/* Screenshot routes - for generating screenshots with mock data */}
      <Route path="/s/dashboard" element={<ScreenshotDashboard />} />
      
      {/* Avoid infinite redirects by having an exact route for the callback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </AppLayout>
    </PasswordCheck>
    </ToastProvider>
  )
}

export default App